/* eslint-disable camelcase */
import _ from 'lodash';
import moment from 'moment-timezone';
import settings from 'settings';
import Logger from 'config/logging';
import { djangoDurationToMoment } from 'library/time';
import {
  perfNow,
  isSetServerItemsDetailEnabled,
  perfEvent
} from 'libs/perf/rosterRenderPerf';
import { ROSTER_TIMELINE } from 'actions/actionTypes';
import { isLeaveItem, isTravelItem } from 'selectors/roster.selectors';

const types = ROSTER_TIMELINE;

// Row id (group.id) for the group whose uid matches the assignment's user_id.
const rowIdForUser = (userGroups, userId) => {
  const group = userGroups.find(g => g.uid === userId);
  return group ? group.id : null;
};

// Build the clipboard payload: each item carries offsets relative to the
// reference (earliest start_time; tie-break lowest row id).
export const buildClipboardItems = (assignments, userGroups) => {
  if (!assignments.length) {
    return [];
  }
  const withMeta = assignments.map(a => ({
    a,
    start: moment(a.start_time).valueOf(),
    end: moment(a.end_time).valueOf(),
    rowId: rowIdForUser(userGroups, a.user_id)
  }));
  const reference = withMeta.reduce((ref, cur) => {
    if (cur.start < ref.start) {
      return cur;
    }
    if (cur.start === ref.start && (cur.rowId ?? Infinity) < (ref.rowId ?? Infinity)) {
      return cur;
    }
    return ref;
  }, withMeta[0]);

  return withMeta.map(({
    a, start, end, rowId
  }) => ({
    relTimeOffset: start - reference.start,
    relRowOffset: (rowId ?? 0) - (reference.rowId ?? 0),
    duration: end - start,
    title: a.title,
    activity: a.activity ? { ...a.activity } : a.activity,
    color: a.color,
    tags: a.tags || [],
    default_notifications: a.default_notifications || []
  }));
};

// Resolve clipboard items to concrete paste targets at the given anchor.
// Skips any item whose target row does not exist (out of bounds).
export const computePasteTargets = (clipboard, userGroups, anchorRowId, anchorTime, rosterVersionId) => {
  if (!clipboard || clipboard.rosterVersionId !== rosterVersionId) {
    return [];
  }
  const targets = [];
  clipboard.items.forEach((item) => {
    const targetRowId = anchorRowId + item.relRowOffset;
    const group = userGroups.find(g => g.id === targetRowId);
    if (!group) {
      return;
    }
    const start = anchorTime + item.relTimeOffset;
    targets.push({
      user_id: group.uid,
      user: { ...group },
      start_time: start,
      end_time: start + item.duration,
      title: item.title,
      activity: item.activity,
      color: item.color,
      tags: item.tags,
      default_notifications: item.default_notifications
    });
  });
  return targets;
};

const initialState = {
  meta: {
    id: 0,
    name: '',
    add: 0,
    mod: 0,
    del: 0
  },
  user_groups: [],
  activity_groups: [],
  server_items: [],
  local_items: [],
  deleted_items: [],
  travel_items: [],
  leave_items: [],
  availability_items: [],
  selected: [],
  pinnedActivities: [],
  clipboard: null
};

export const rosterTimeline = (state = initialState, action) => {
  switch (action.type) {
  case types.SET_SERVER_ITEMS: {
    const { items } = action;
    const detailEnabled = isSetServerItemsDetailEnabled();
    const reducerStart = detailEnabled ? perfNow() : null;
    let groupIndexStart = null;
    let groupIndexMs = 0;
    let lookupMs = 0;
    let momentMs = 0;
    let objectBuildMs = 0;

    if (detailEnabled) groupIndexStart = perfNow();
    // Index groups by uid once (O(U)) so the per-item lookup is O(1) instead of a
    // linear scan — the whole map is then O(A+U) rather than O(A*U). On a full roster
    // (A assignments x ~1000 groups) this is a large saving.
    const groupByUid = new Map(state.user_groups.map(g => [g.uid, g]));
    if (detailEnabled) groupIndexMs = perfNow() - groupIndexStart;

    const epochByTimestamp = new Map();
    let timestampCount = 0;
    let uniqueValidTimestampCount = 0;
    let timestampCacheHitCount = 0;

    const toIndependentMoment = (value) => {
      timestampCount++;
      if (typeof value !== 'string') {
        return moment(value);
      }
      if (epochByTimestamp.has(value)) {
        timestampCacheHitCount++;
        return moment(epochByTimestamp.get(value));
      }
      const parsed = moment(value);
      if (parsed.isValid()) {
        epochByTimestamp.set(value, parsed.valueOf());
        uniqueValidTimestampCount++;
      }
      return parsed;
    };

    const mappedItems = items.map((i) => {
      const lookupStart = detailEnabled ? perfNow() : null;
      const itemUid = i.user_id === null ? -1 : i.user_id;
      const group = groupByUid.get(itemUid);
      if (detailEnabled) lookupMs += perfNow() - lookupStart;
      if (group === undefined) {
        // The item's row isn't in the current view — e.g. an unallocated assignment
        // (user_id null -> -1) while "hide unallocated rows" is on, which removes the -1
        // group from user_groups. Skip it instead of throwing. A throw here rejects the
        // entire SET_SERVER_ITEMS dispatch (silently swallowed by loadRosterUserAssignments'
        // rejection handler), so server_items never updates and the timeline stops
        // refreshing on pan/zoom with no error. getUserGroupItems already filters
        // non-visible groups the same way at render time.
        return null;
      }

      const momentStart = detailEnabled ? perfNow() : null;
      const start_time = toIndependentMoment(i.start);
      const end_time = toIndependentMoment(i.end);
      if (detailEnabled) momentMs += perfNow() - momentStart;

      const objectBuildStart = detailEnabled ? perfNow() : null;
      const mappedItem = {
        id: i.id,
        user_id: group.uid,
        user: { ...i.user },
        title: i.activity.name,
        start_time,
        end_time,
        notes: i.notes,
        admin_notes: i.admin_notes,
        available_sign_on: i.available_sign_on,
        itemProps: { style: { backgroundColor: i.activity.color } },
        color: i.activity.color,
        activity: { ...i.activity },
        notifications: i.notifications,
        tags: i.tags || [],
        validations: i.valdiations || [],
        latest_action: i.latest_action || null,
        has_unresolved_actions: i.has_unresolved_actions || false
      };
      if (detailEnabled) objectBuildMs += perfNow() - objectBuildStart;
      return mappedItem;
    });
    const nextState = { ...state, server_items: mappedItems.filter(Boolean) };

    if (detailEnabled) {
      const reducerBodyMs = perfNow() - reducerStart;
      perfEvent('SET_SERVER_ITEMS-detail', {
        itemCount: items.length,
        timestampCount,
        uniqueValidTimestampCount,
        timestampCacheHitCount,
        groupIndexMs,
        lookupMs,
        momentMs,
        objectBuildMs,
        reducerBodyMs,
        unattributedMs: Math.max(
          0,
          reducerBodyMs - groupIndexMs - lookupMs - momentMs - objectBuildMs
        )
      });
    }

    return nextState;
  }

  case types.UPDATE_ASSIGNMENT_ACTION: {
    const { assignmentId, latest_action, has_unresolved_actions } = action;
    return {
      ...state,
      server_items: state.server_items.map((item) => {
        if (item.id === assignmentId) {
          return { ...item, latest_action, has_unresolved_actions };
        }
        return item;
      })
    };
  }

  case types.SET_TRAVEL_ITEMS: {
    const items = action.items.results;
    const itineraries = items.reduce((objects, obj) => {
      objects[obj.itinerary.id] = (objects[obj.itinerary.id] || []).concat(obj); // eslint-disable-line
      return objects;
    }, {});
    const travelItems = Object.values(itineraries)
      .map((itinerary) => {
        const id = `t-${itinerary[0].itinerary.id}`;
        const start_time = itinerary.reduce((prev, current) => (prev.departure_date < current.departure_date ? prev : current));
        const end_time = itinerary.reduce((prev, current) => (prev.arrival_date > current.arrival_date ? prev : current));
        // Need to force timezone to be the arrival/dept location's
        return {
          id,
          start_time: moment.tz(start_time.departure_date.substr(0, 19), start_time.leg.route.departure_location.timezone),
          end_time: moment.tz(end_time.arrival_date.substr(0, 19), end_time.leg.route.arrival_location.timezone),
          user_id: itinerary[0].itinerary.user.id,
          user: itinerary[0].itinerary.user,
          travel_type: itinerary[0].itinerary.travel_type,
          reference_number: itinerary[0].itinerary.reference_number,
          legs: itinerary.map(item => ({
            ...item.leg,
            status: item.status,
            departure_date: item.departure_date,
            arrival_date: item.arrival_date,
            reference_number: `[${item.reference_number}] ${item.leg.reference_number}`
          })),
          notes: itinerary[0].itinerary.notes
        };
      });

    return { ...state, travel_items: travelItems };
  }

  case types.SET_LEAVE_ITEMS: {
    const items = action.items.results;
    const leaveItems = items.map((i) => {
      const id = `l-${i.id}`;
      return {
        ...i,
        id,
        start_time: i.start_datetime,
        end_time: i.end_datetime,
        user_id: i.user
      };
    });
    return { ...state, leave_items: leaveItems };
  }

  case types.SET_AVAILABILITY_ITEMS: {
    const { items } = action;
    return { ...state, availability_items: items };
  }

  // Set the time groups prop to the incoming user payload
  case types.SET_USER_GROUPS: {
    const { hideEmpty, hideUnallocatedRows } = action;
    // if hideUnallocated false then not working.
    const {
      user_groups,
      local_items,
      server_items,
      travel_items,
      leave_items,
      deleted_items
    } = state;

    // Get user ids with activities
    const union = _.unionBy(local_items, server_items, travel_items, leave_items, 'id');

    const filtered = union.filter(item => !deleted_items.includes(item.id)); // no deleted
    const inUseUids = [...new Set(filtered.map(i => i.user_id))];
    // Fetch a list of pinned users
    const pinnedGroups = user_groups.filter(g => g.pinNumber > 1);
    const pinnedUserIds = pinnedGroups.map(p => p.uid);

    // Remove Pinned (they're added later)
    // Filter out empty if need be
    const filteredUsers = action.users.filter((u) => {
      const isPinned = pinnedUserIds.indexOf(u.id) !== -1;
      const isEmpty = inUseUids.indexOf(u.id) === -1;
      if (isPinned || (hideEmpty && isEmpty)) {
        return false;
      }
      return true;
    })
      .map(user => ({
        uid: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        title: `${user.last_name}, ${user.first_name}`,
        active: user.is_active && user.is_tenancy_active
      }));

    // Combine the pinned users with the new user list placing the pinned users at the top of the list
    // Determine starting ID based on whether unallocated is shown
    let id = hideUnallocatedRows ? 0 : 1;
    const newGroups = pinnedGroups.concat(filteredUsers)
      .map(g => ({
        ...g,
        id: id++
      }));

    // Add the unallocated group to the front of the group list
    if (!hideUnallocatedRows) {
      newGroups.unshift({
        id: 0,
        uid: -1,
        title: 'UNALLOCATED',
        active: true,
        pinNumber: 1
      });
    }

    return { ...state, user_groups: newGroups };
  }

  case types.SET_ACTIVITY_GROUPS: {
    const { hideEmpty, hideUnallocatedRows } = action;

    const {
      local_items,
      server_items,
      travel_items,
      leave_items,
      deleted_items
    } = state;

    // Get user ids with activities
    const union = _.unionBy(local_items, server_items, travel_items, leave_items, 'id');
    const filtered = _.filter(union, item => !_.includes(deleted_items, item.id)
      && !isTravelItem(item)
      && !isLeaveItem(item));
    const inUseAids = _.uniq(filtered.map(i => i.activity.id));

    let id = 0;
    const mappedActivities = action.activities.filter((a) => {
      const isEmpty = inUseAids.indexOf(a.id) === -1;
      return !(hideEmpty && isEmpty && hideUnallocatedRows);
    })
      .map(activity => ({
        ...activity,
        id: id++,
        aid: activity.id,
        title: activity.name,
        active: activity.active,
        activity
      }));

    return { ...state, activity_groups: mappedActivities };
  }

  case types.TOGGLE_GROUP_PIN: {
    const user_groups = _.cloneDeep(state.user_groups);
    const newGroup = _.clone(action.group);
    const groupIndex = user_groups.findIndex(group => group.uid === newGroup.uid);

    if (groupIndex === -1) {
      Logger.warn(`Toggle pin could not find group for user id ${newGroup.uid}`);
      return state;
    }
    if (newGroup.pinNumber) {
      newGroup.pinNumber = null;
    }
    else {
      // May result in holes in number eg: [1, 2, 5, 6] - but this doesnt matter
      const nextPinNumber = _.maxBy([...user_groups, { pinNumber: 1 }], 'pinNumber').pinNumber + 1;
      newGroup.pinNumber = nextPinNumber;
    }

    // Remove the item
    let newGroups = [];
    newGroups = [
      ...user_groups.slice(0, groupIndex),
      ...user_groups.slice(groupIndex + 1)
    ];

    // Add the item
    newGroups.push(newGroup);

    // Re-sort groups
    newGroups = _.sortBy(newGroups, 'pinNumber');
    for (let i = 0; i < newGroups.length; i += 1) {
      newGroups[i].id = i;
    }

    return { ...state, user_groups: newGroups };
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.SET_DEFAULT_META: {
    return {
 ...initialState,
local_items: [],
      deleted_items: []
};
  }

  case types.CLEAR_LOCAL_ITEMS: {
    return {
 ...state,
local_items: [],
      deleted_items: []
};
  }

  case types.CLEAR_SERVER_ITEMS: {
    return { ...state, server_items: [] };
  }

  case types.CLEAR_TRAVEL_ITEMS: {
    return { ...state, travel_items: [] };
  }

  case types.CLEAR_LEAVE_ITEMS: {
    return { ...state, leave_items: [] };
  }

  case types.CLEAR_AVAILABILITY_ITEMS: {
    return { ...state, availability_items: [] };
  }

  case types.DELETE_TIMELINE_ACTIVITIES: {
    // delete selected item
    // TODO: Change to item*S* when multiselect is implemented
    const itemIds = state.selected;
    const localItems = _.cloneDeep(state.local_items);
    const deletedItems = _.cloneDeep(state.deleted_items);

    _.forEach(itemIds, (itemId) => {
      let item = null;
      if (_.find(localItems, i => i.id === itemId)) {
        item = localItems.filter(localItem => localItem.id === itemId)
          .pop();
        _.remove(localItems, i => i === item);
      }
      if (itemId > 0) {
        deletedItems.push(itemId);
      }
    });
    return {
 ...state,
deleted_items: _.uniq(deletedItems),
      local_items: localItems
};
  }

  case types.SELECT_TIMELINE_ACTIVITIES: {
    return { ...state, selected: action.ids };
  }

  case types.COPY_ASSIGNMENTS: {
    const { ids, rosterVersionId } = action;
    if (!ids.length) {
      return state;
    }
    const pool = [...state.server_items, ...state.local_items];
    const resolved = pool.filter(item => ids.includes(item.id));
    if (!resolved.length) {
      return state;
    }
    return {
      ...state,
      clipboard: {
        rosterVersionId,
        items: buildClipboardItems(resolved, state.user_groups)
      }
    };
  }

  case types.PASTE_ASSIGNMENTS: {
    const { anchorRowId, anchorTime, rosterVersionId } = action;
    const targets = computePasteTargets(state.clipboard, state.user_groups, anchorRowId, anchorTime, rosterVersionId);
    if (!targets.length) {
      return state;
    }
    const items = _.cloneDeep(state.local_items);
    const existingIds = items.map(i => i.id);
    let newItemId = Math.min(0, ...existingIds) - 1;
    targets.forEach((t) => {
      items.push({
        id: newItemId -= 1,
        user_id: t.user_id,
        user: t.user,
        title: t.title,
        start_time: t.start_time,
        end_time: t.end_time,
        notes: '',
        admin_notes: '',
        itemProps: { style: { backgroundColor: t.color } },
        color: t.color,
        activity: t.activity ? { ...t.activity } : t.activity,
        default_notifications: t.default_notifications,
        notifications: [],
        tags: t.tags,
        validations: []
      });
    });
    return { ...state, local_items: items };
  }

  case types.CLEAR_CLIPBOARD: {
    return { ...state, clipboard: null };
  }

  case types.ADD_ACTIVITY_TO_TIMELINE: {
    const {
      group, activity, time, createActivityCount, hoursBetween
    } = action;

    const items = _.cloneDeep(state.local_items);
    // Find the highest existing id and increment for a new id.
    // This seems easier than filling in the missing numbers in the array.
    const existingIds = items.map(i => i.id);
    // -ve ids = Local. +ve = Server
    let newItemId = Math.min(0, ...existingIds) - 1;
    // if multiple activities were specified.
    for (let i = 0; i < createActivityCount; i++) {
      const startTime = moment(time)
        .add(i * hoursBetween, 'hours');
      const activityDuration = djangoDurationToMoment(activity.duration);
      const endTime = moment(startTime)
        .add(activityDuration);
      const user = _.find(state.user_groups, ug => ug.uid === group.uid);
      const newItem = {
        id: newItemId -= 1,
        user_id: group.uid,
        user: { ...user },
        title: activity.name,
        start_time: startTime.valueOf(),
        end_time: endTime.valueOf(),
        notes: '',
        admin_notes: '',
        itemProps: { style: { backgroundColor: activity.color } },
        color: activity.color,
        activity: { ...activity },
        default_notifications: activity.default_notifications,
        notifications: [],
        tags: [],
        validations: []
      };
      items.push(newItem);
    }
    return { ...state, local_items: items };
  }

  case types.SWAP_ASSIGNMENTS: {
    const { ids, items } = action;
    let serverItems = [...state.server_items];
    let localItems = [...state.local_items];

    // iterate through ids
    ids.forEach((id) => {
      // check server
      let index = serverItems.findIndex(item => item.id === id);
      if (index > -1) {
        // Remove the item from the list
        serverItems = [
          ...serverItems.slice(0, index),
          ...serverItems.slice(index + 1)
        ].concat(items);
      }

      index = localItems.findIndex(item => item.id === id);

      if (index > -1) {
        // Remove the item from the list
        localItems = [
          ...localItems.slice(0, index),
          ...localItems.slice(index + 1)
        ].concat(items);
      }
    });

    return {
      ...state,
      local_items: [...new Set(localItems)],
      server_items: [...new Set(serverItems)]
    };
  }

  case types.MODIFY_LOCAL_ACTIVITIES: {
    const { items } = action;
    let localItems = [...state.local_items];
    items.forEach((newItem) => {
      const index = localItems.findIndex(item => item.id === newItem.id);

      if (index > -1) {
        // Remove the item from the list
        localItems = [
          ...localItems.slice(0, index),
          ...localItems.slice(index + 1)
        ];
      }

      // Add the item
      localItems.push({
        ...newItem,
        title: newItem.activity.name,
        color: newItem.activity.color
      });
    });
    return {
      ...state,
      local_items: localItems
    };
  }

  case types.MODIFY_LOCAL_TRAVEL: {
    const { items } = action;
    let travelItems = [...state.travel_items];
    items.forEach((newItem) => {
      const index = travelItems.findIndex(item => item.id === newItem.id);

      if (index > -1) {
        // Remove the item from the list
        travelItems = [
          ...travelItems.slice(0, index),
          ...travelItems.slice(index + 1)
        ];
      }

      // Add the item
      travelItems.push({ ...newItem });
    });
    return {
      ...state,
      travel_items: travelItems
    };
  }

  case types.ADD_BULK_ACTIVITIES: {
    const { activities } = action;
    const items = _.cloneDeep(state.local_items);
    const existingIds = items.map(i => i.id);

    // -ve ids = Local. +ve = Server
    let newItemId = Math.min(0, ...existingIds) - 1;
    activities.forEach((activity) => {
      const newItem = {
        id: newItemId--,
        user_id: activity.user_id,
        user: { ...activity.user },
        title: activity.activity.name,
        start_time: moment(activity.start_time),
        end_time: moment(activity.end_time),
        notes: '',
        admin_notes: '',
        itemProps: { style: { backgroundColor: activity.color } },
        color: activity.activity.color,
        activity: { ...activity.activity },
        default_notifications: activity.default_notifications,
        tags: activity.tags || [],
        validations: activity.valdiations || []
      };
      items.push(newItem);
    });
    return { ...state, local_items: items };
  }

  case types.REMOVE_BULK_ACTIVITIES_RANGE: {
    // Destructive editing
    const { activities } = action;
    const { local_items, server_items } = state;
    let newLocalItems = _.cloneDeep(local_items);
    let deleted_items = _.cloneDeep(state.deleted_items);
    const activities_by_user = _.groupBy(activities, 'user_id');

    function activityInBoundry(a_start, a_end, boundry_start, boundry_end) {
      const inside = a_start.isSameOrAfter(boundry_start) && a_end.isSameOrBefore(boundry_end);
      const inLeft = a_start.isSameOrBefore(boundry_start) && a_end.isAfter(boundry_start);
      const inRight = a_start.isBefore(boundry_end) && a_end.isSameOrAfter(boundry_end);
      return inside || inLeft || inRight;
    }

    _.forEach(activities_by_user, (activities_for_user, user) => {
      const min_start = moment.tz(_.minBy(activities_for_user, 'start_time').start_time, moment.tz.guess());
      const max_end = moment.tz(_.maxBy(activities_for_user, 'end_time').end_time, moment.tz.guess());

      // Delete local items
      const deleted_local_ids = _.filter(local_items, i => String(i.user_id) === user && activityInBoundry(i.start_time, i.end_time, min_start, max_end))
        .map(a => a.id);
      const deleted_server_ids = _.filter(server_items, i => String(i.user_id) === user && activityInBoundry(i.start_time, i.end_time, min_start, max_end))
        .map(a => a.id);

      newLocalItems = _.filter(newLocalItems, i => !_.includes(deleted_local_ids, i.id));
      deleted_items = _.union(deleted_items, deleted_server_ids);
    });
    return {
 ...state,
local_items: newLocalItems,
      deleted_items
};
  }
  // /////////////////////////////////////////////////////////////////////////

  case types.RESIZE_ACTIVITIES: {
    const { items, changes } = action;
    const localItems = _.cloneDeep(state.local_items);
    const serverItems = _.cloneDeep(state.server_items);

    _.forEach(items, (newItem) => {
      const localItem = _.find(localItems, i => i.id === newItem.key);
      const minMins = settings.APP_CONFIG.minActivityDurationMinutes;
      if (localItem) {
        _.remove(localItems, i => i === localItem);
        if (changes.isStartTimeChange) {
          const minStart = newItem.end.clone()
            .subtract(minMins, 'minutes');
          localItem.start_time = moment.min(minStart, newItem.start);
          localItem.end_time = newItem.end;
        }
        else {
          const maxEnd = newItem.start.clone()
            .add(minMins, 'minutes');
          localItem.start_time = newItem.start;
          localItem.end_time = moment.max(maxEnd, newItem.end);
        }
        localItems.push(localItem);
      }
      else {
        const item = _.find(serverItems, i => i.id === newItem.key);
        if (changes.isStartTimeChange) {
          const minStart = newItem.end.clone()
            .subtract(minMins, 'minutes');
          item.start_time = moment.min(minStart, newItem.start);
          item.end_time = newItem.end;
        }
        else {
          const maxEnd = newItem.start.clone()
            .add(minMins, 'minutes');
          item.start_time = newItem.start;
          item.end_time = moment.max(maxEnd, newItem.end);
        }
        localItems.push(item);
      }
    });
    return { ...state, local_items: localItems };
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.MOVE_ACTIVITIES: {
    const { changes, items, user_groups } = action;
    const localItems = _.cloneDeep(state.local_items);
    const serverItems = _.cloneDeep(state.server_items);

    _.forEach(items, (newItem) => {
      const localItem = _.find(localItems, i => i.id === newItem.key);
      if (localItem) {
        _.remove(localItems, i => i === localItem);
        const user = _.find(user_groups, g => g.id === newItem.row);
        // New (unsaved) local items store start/end as epoch-ms numbers, not Moments, so
        // call .add() via moment() rather than on the stored value directly - a raw number
        // has no .add() and would throw, aborting the drop mid-drag (item stuck to cursor).
        // moment(existingMoment) simply clones, so this is a no-op for already-Moment times.
        localItem.start_time = moment(localItem.start_time).add(changes.timeDelta, 'minutes');
        localItem.end_time = moment(localItem.end_time).add(changes.timeDelta, 'minutes');
        localItem.user = { ...user };
        localItem.user_id = user.uid;
        localItems.push(localItem);
      }
      else {
        const item = _.find(serverItems, i => i.id === newItem.key);
        const user = _.find(user_groups, g => g.id === newItem.row);
        item.start_time = moment(item.start_time).add(changes.timeDelta, 'minutes');
        item.end_time = moment(item.end_time).add(changes.timeDelta, 'minutes');
        item.user = { ...user };
        item.user_id = user.uid;
        localItems.push(item);
      }
    });
    return { ...state, local_items: localItems };
  }

  case types.UPDATE_PINNED_ACTIVITIES: {
    return { ...state, pinnedActivities: action.activities };
  }

  default:
    return state;
  }
};
